import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from './SEOHead';

const {
  FiArrowLeft, FiArrowRight, FiUser, FiEdit3, FiRefreshCw, FiLayers, FiShield, FiLock, FiCheckCircle, FiBookOpen,
} = FiIcons;

/* per-step visuals (match the site's card style) */
const AuthorViz = () => (
  <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
    <div className="text-[10px] font-bold uppercase text-gray-400 mb-3">Author Persona · who you are</div>
    {[['Background', 'Ukrainian-born, raised in an internat'], ['Lived experience', 'Orphanhood · emigration · faith'], ['Domain', 'Memoir · identity']].map(([k, v]) => (
      <div key={k} className="flex items-start gap-2 text-sm py-1.5 border-b border-gray-50 last:border-0">
        <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-emerald-500 mt-0.5" />
        <div><span className="text-gray-400 text-[11px] uppercase mr-2">{k}</span><span className="text-gray-800">{v}</span></div>
      </div>
    ))}
    <div className="mt-2 text-[11px] text-gray-400">From your books + what you tell us — never invented.</div>
  </div>
);
const StyleViz = () => (
  <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
    <div className="text-[10px] font-bold uppercase text-gray-400 mb-3">Style DNA · how you write</div>
    {[['Build–release rhythm', 78], ['Restraint', 71], ['Imagery', 64], ['Dialogue', 38]].map(([l, v]) => (
      <div key={l} className="mb-2"><div className="flex justify-between text-[11px] text-gray-500 mb-1"><span>{l}</span><span>{v}</span></div>
        <div className="w-full bg-gray-100 rounded-full h-2"><div className="h-2 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500" style={{ width: `${v}%` }} /></div></div>
    ))}
    <div className="mt-1 flex flex-wrap gap-1.5">{['opens small → widens', 'participial openings', 'em-dash asides'].map((t) => <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-secondary-50 text-secondary-700">{t}</span>)}</div>
  </div>
);
const LearnViz = () => (
  <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
    <div className="text-[10px] font-bold uppercase text-gray-400 mb-3">What it learned from your edits</div>
    {[['Prefers “use” over “utilize”', '0.94', '12×'], ['Shortens long openers', '0.71', '4×'], ['Keeps a short beat after a long line', '0.83', '7×']].map(([r, c, n]) => (
      <div key={r} className="flex items-center gap-2 text-sm py-1.5 border-b border-gray-50 last:border-0">
        <SafeIcon icon={FiRefreshCw} className="w-4 h-4 text-primary-500" />
        <span className="text-gray-800 flex-1">{r}</span>
        <span className="text-[10px] text-gray-400">{n} · {c}</span>
      </div>
    ))}
    <div className="mt-2 text-[11px] text-gray-400">Every choice you make teaches it. You confirm; it remembers.</div>
  </div>
);
const AcrossViz = () => (
  <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
    <div className="flex items-center gap-3">
      {['Book 1', 'Book 2', 'Book 3'].map((b, i) => (
        <React.Fragment key={b}>
          <div className={`flex-1 rounded-xl p-3 text-center ${i === 0 ? 'bg-primary-50 border border-primary-200' : 'bg-gray-50 border border-gray-100'}`}>
            <SafeIcon icon={FiBookOpen} className={`w-5 h-5 mx-auto mb-1 ${i === 0 ? 'text-primary-600' : 'text-gray-400'}`} />
            <div className="text-[11px] font-semibold text-gray-700">{b}</div>
          </div>
          {i < 2 && <SafeIcon icon={FiArrowRight} className="w-4 h-4 text-gray-300" />}
        </React.Fragment>
      ))}
    </div>
    <div className="mt-3 text-[11px] text-gray-500 text-center">Your persona carries forward — book two starts knowing you.</div>
  </div>
);
const NoFabViz = () => (
  <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
    <div className="flex items-center gap-2 text-sm text-emerald-700 mb-2"><SafeIcon icon={FiShield} className="w-4 h-4" /> Grounded in you</div>
    <div className="space-y-1.5 text-sm">
      <div className="flex items-center gap-2 text-gray-700"><SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-emerald-500" /> Real background, real facts, real comps</div>
      <div className="flex items-center gap-2 text-gray-400 line-through">Invented bios · fake quotes · made-up details</div>
    </div>
    <div className="mt-2 text-[11px] text-gray-400">Non-fiction stays factual. In fiction, only <em>your</em> invented world is invented — never passed off as fact.</div>
  </div>
);
const ControlViz = () => (
  <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
    <div className="text-[10px] font-bold uppercase text-gray-400 mb-3">About You — yours to edit</div>
    {['Bio & background', 'Voice & style rules', 'Words you avoid'].map((r) => (
      <div key={r} className="flex items-center gap-2 text-sm py-1.5 border-b border-gray-50 last:border-0">
        <span className="text-gray-800 flex-1">{r}</span>
        <span className="text-[11px] px-2 py-0.5 rounded bg-gray-100 text-gray-600 inline-flex items-center gap-1"><SafeIcon icon={FiEdit3} className="w-3 h-3" /> edit</span>
        <span className="text-[11px] px-2 py-0.5 rounded bg-primary-50 text-primary-700">✦ refine</span>
      </div>
    ))}
    <div className="mt-2 text-[11px] text-gray-400 inline-flex items-center gap-1"><SafeIcon icon={FiLock} className="w-3 h-3" /> Private. Never shared or trained across other authors.</div>
  </div>
);

const STEPS = [
  { icon: FiUser, kicker: 'It learns who you are', title: 'Your Author Persona', emotional: 'Your background, your lived experience, the world you come from — drawn from your own books and what you tell us. This is what makes the writing ring true, not generic.', viz: AuthorViz },
  { icon: FiEdit3, kicker: 'It learns how you write', title: 'Your Style DNA', emotional: 'Your rhythm, your restraint, the way you open small and widen into history. Not a style to copy — your fingerprint, captured so every suggestion sounds like you.', viz: StyleViz },
  { icon: FiRefreshCw, kicker: 'It sharpens with your work', title: 'It gets more you over time', emotional: 'Feed it more of your writing — chapters, past books, background — and the profile sharpens. The more of your work it sees, the more the suggestions sound like you specifically.', viz: LearnViz },
  { icon: FiLayers, kicker: 'One persona for your account', title: 'Book two starts knowing you', emotional: 'Your persona isn\'t locked to one manuscript — it lives on your account. When you begin your next book, the voice profile you\'ve built is already there, so you don\'t start from a blank, generic model.', viz: AcrossViz },
  { icon: FiShield, kicker: 'It never makes things up', title: 'Grounded in you — never fabricated', emotional: 'This is where other AI tools fail: they invent plausible-sounding bios, quotes, and facts. We don\'t. Your background and details are real or absent. The only invented thing allowed is your own fiction.', viz: NoFabViz },
  { icon: FiLock, kicker: 'It stays yours', title: 'You can see and correct everything', emotional: 'Every part of your persona is visible and editable — by hand, or refined with AI that you approve. Nothing changes silently. It\'s private, and never used to train anyone else\'s writing.', viz: ControlViz },
];

const AuthorPersonaPage = () => {
  const structuredData = {
    '@context': 'https://schema.org', '@type': 'WebPage',
    name: 'Your Author Persona — how ManuscriptSmith learns to write like you',
    description: 'ManuscriptSmith builds a private Author Persona and Style DNA from your books and background, sharpens as you add more of your work, lives on your account for every book, and never fabricates.',
    url: 'https://manuscriptsmith.com/author-persona',
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-parchment-50 to-parchment-100">
      <SEOHead
        title="Your Author Persona — Write Like Yourself, Across Every Book | ManuscriptSmith"
        description="ManuscriptSmith builds a private Author Persona + Style DNA from your books and background, sharpens as you add more of your work, lives on your account for every book, and never fabricates facts. The voice engine other AI tools don't have."
        keywords="author voice profile, write like yourself, author style ai, personal writing ai, ai that learns your voice, no fabrication ai writing, author persona, style dna"
        canonicalUrl="https://manuscriptsmith.com/author-persona"
        structuredData={structuredData}
      />

      <main>
        {/* Hero */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5">
            It learns <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">who you are</span> — and writes like you, across every book.
          </h1>
          <p className="text-xl text-gray-600">
            Most AI writes like everyone. ManuscriptSmith builds a private <strong>Author Persona</strong> from your own
            books and background, sharpens it as you add more of your work, and keeps it on your account for your
            next book — and it never makes things up.
          </p>
        </section>

        {/* Steps */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          {STEPS.map((s, i) => {
            const Viz = s.viz;
            const flip = i % 2 === 1;
            return (
              <div key={s.title} className="py-8 border-b border-parchment-200/60 last:border-0">
                <div className={`grid md:grid-cols-2 gap-8 items-center ${flip ? 'md:[direction:rtl]' : ''}`}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }}
                    className="md:[direction:ltr]">
                    <div className="inline-flex items-center gap-2 text-primary-600 font-semibold text-sm mb-2">
                      <SafeIcon icon={s.icon} className="w-5 h-5" /> {s.kicker}
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">{s.title}</h2>
                    <p className="text-lg text-gray-600">{s.emotional}</p>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.1 }}
                    className="md:[direction:ltr]">
                    <Viz />
                  </motion.div>
                </div>
              </div>
            );
          })}
        </section>

        {/* The feeling */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <SafeIcon icon={FiBookOpen} className="w-10 h-10 text-primary-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">A co-writer that actually knows you.</h2>
          <p className="text-lg text-gray-600">
            Not a generic model guessing at a voice — a private persona built from your real work, that grows as
            you feed it more of your writing and never invents a thing. That's the difference between sounding like AI and sounding like you.
          </p>
          <Link to="/how-we-read-your-book" className="inline-flex items-center mt-6 text-primary-600 font-semibold hover:underline">
            See how we read your book <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-1" />
          </Link>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-10 text-center text-white shadow-xl">
            <h2 className="text-3xl font-bold mb-4">Build your Author Persona</h2>
            <p className="text-lg opacity-90 mb-8">Import a book or a sample. We'll start learning your voice, and it's yours to see and edit.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup" className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 font-bold text-lg rounded-lg hover:bg-gray-50">
                Get started <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/analysis" className="inline-flex items-center justify-center px-8 py-4 bg-white/15 border border-white/40 text-white font-semibold text-lg rounded-lg hover:bg-white/25">
                See a sample analysis
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AuthorPersonaPage;
